import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import { LoginStatus } from '../../redux/slices/userSlice'
import { useTranslation } from '../../hooks/useTranslation'
import { RootState } from '../../redux/store'
import { closeModalWithDelay, openModal, openModalWithDelay } from '../../redux/slices/modalSlice'
import { ModalType } from '../../core/enums/Modals'
import { TLevel, TRaffle } from '@smartico/public-api/dist/WSAPI/WSAPITypes'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import Utils from '../../core/Utils'
import { gameWidgetUrlForPath } from '../../core/widgets'
import { reInitDataForLanguage } from '../../redux/initThunks'
import { Smartico } from '../../core/Smartico'
import {
	ChevronDownIcon,
	CrownIcon,
	DiamondIcon,
	EnvelopeIcon,
	GemIcon,
	PointsCoinIcon,
	TicketIcon,
} from '../../img/svgComponents/UiIcons'

// Popovers anchor under the centre of the pill that opened them (top and anchorX in page coordinates)
export interface PopoverAnchor {
	top: number
	anchorX: number
}

const anchorFor = (el: HTMLElement | null): PopoverAnchor | undefined => {
	if (!el) return undefined
	const rect = el.getBoundingClientRect()
	return { top: rect.bottom + window.scrollY + 12, anchorX: rect.left + window.scrollX + rect.width / 2 }
}

const formatCount = (value?: number) => (value || 0).toLocaleString('en-US')

// A raffle's draws share the player's tickets, so count the largest draw per raffle
const countRaffleTickets = (raffles: TRaffle[] = []) =>
	raffles.reduce((sum, raffle) => sum + Math.max(0, ...(raffle.draws || []).map((draw) => draw.my_tickets_count || 0)), 0)

export default function Header() {
	const isMobile = Utils.isMobile()
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const location = useLocation()

	const { loggedIn } = useAppSelector((state: RootState) => state.user)
	const profile = useAppSelector((state: RootState) => state.userPublic.profile)
	const levels = useAppSelector((state: RootState) => state.level.levels)
	const wallet = useAppSelector((state: RootState) => state.wallet)
	const modal = useAppSelector((state: RootState) => state.modal)
	const messages = useAppSelector((state: RootState) => state.inbox.messages)

	const [currentLevel, setCurrentLevel] = useState<TLevel>()
	const [raffleTickets, setRaffleTickets] = useState(0)

	useEffect(() => {
		if (profile && levels) {
			const defaultLevel = levels[0]

			setCurrentLevel(levels.find((level: TLevel) => level.id === profile.ach_level_current_id) || defaultLevel)
		}
	}, [profile, levels, profile?.ach_level_current_id])

	useEffect(() => {
		if (loggedIn !== LoginStatus.LoggedIn) return
		let active = true
		const update = (raffles: TRaffle[]) => active && setRaffleTickets(countRaffleTickets(raffles))

		Smartico.apiReady
			.then((api) => api.getRaffles({ onUpdate: update }))
			.then(update)
			.catch(() => {})

		return () => {
			active = false
		}
	}, [loggedIn])

	const unreadMessagesCount = useMemo(() => {
		return messages.filter((message) => !message.read).length
	}, [messages])

	const userButtonRef = useRef<HTMLButtonElement>(null)
	const inboxButtonRef = useRef<HTMLButtonElement>(null)
	const balanceButtonRef = useRef<HTMLButtonElement>(null)
	const headerRef = useRef<HTMLDivElement>(null)

	// The mobile sidebar drawer opens right under the header, whose height changes with its layout
	useEffect(() => {
		const el = headerRef.current
		if (!el || typeof ResizeObserver === 'undefined') return
		const observer = new ResizeObserver(() => document.documentElement.style.setProperty('--header-height', `${el.offsetHeight}px`))
		observer.observe(el)
		return () => observer.disconnect()
	}, [loggedIn, location.pathname])

	const { getTexts } = useTranslation(true)

	const isBalanceOpen = modal.activeModal === ModalType.Balance && modal.modalProps?.isVisible
	const isUserOpen = modal.activeModal === ModalType.User && modal.modalProps?.isVisible

	const handleOpenLoginModal = (e: React.BaseSyntheticEvent) => {
		e.preventDefault()
		dispatch(openModal({ modalType: ModalType.Login }))
	}

	const handleOpenUserModal = () => {
		if (modal.activeModal === ModalType.User) {
			dispatch(closeModalWithDelay())
		} else {
			dispatch(openModalWithDelay({ modalType: ModalType.User, modalProps: { position: anchorFor(userButtonRef.current) } }))
		}
	}

	const handleOpenBalanceModal = () => {
		if (isMobile) {
			navigate('wallet-page')
			return
		}
		modal.activeModal !== ModalType.Balance
			? dispatch(openModalWithDelay({ modalType: ModalType.Balance, modalProps: { position: anchorFor(balanceButtonRef.current) } }))
			: dispatch(closeModalWithDelay())
	}

	const handleOpenInboxModal = () => {
		if (inboxButtonRef.current && !isMobile) {
			const rect = inboxButtonRef.current.getBoundingClientRect()
			modal.activeModal !== ModalType.Inbox
				? dispatch(
						openModalWithDelay({
							modalType: ModalType.Inbox,
							modalProps: {
								position: {
									top: rect.top + window.scrollY + 25,
									left: rect.left + window.scrollX - 50,
									width: rect.width,
									height: rect.height,
								},
							},
						}),
				  )
				: dispatch(closeModalWithDelay())
		} else {
			dispatch(openModalWithDelay({ modalType: ModalType.Inbox }))
		}
	}

	const handleOpenDepositModal = (e: React.BaseSyntheticEvent) => {
		if (loggedIn === LoginStatus.NotLoggedIn) {
			handleOpenLoginModal(e)
		} else {
			dispatch(openModal({ modalType: ModalType.Cashier }))
		}
	}

	if (location.pathname.includes('/game/') && isMobile) {
		return null
	}

	const currencies = [
		{ key: 'points', label: 'Loyalty points', icon: <PointsCoinIcon size={22} />, value: profile?.ach_points_balance },
		{ key: 'tickets', label: 'Raffle tickets', icon: <TicketIcon size={22} />, value: raffleTickets },
		{ key: 'gems', label: 'Gems', icon: <GemIcon size={22} />, value: profile?.ach_gems_balance },
		{ key: 'diamonds', label: 'Diamonds', icon: <DiamondIcon size={22} />, value: profile?.ach_diamonds_balance },
	]

	return (
		<div className="header" ref={headerRef}>
			<div className="inner-header">
				<div className="left-header-items">
					{isMobile && <Link className="logo-mobile" to="/"></Link>}
					{!isMobile && gameWidgetUrlForPath(location.pathname) && <Link className="logo-desktop" to="/"></Link>}
					{!isMobile && (
						<div className="deposit-btn" onClick={handleOpenDepositModal}>
							{getTexts('deposit')}
						</div>
					)}
					<LanguageDropdown />
				</div>
				<div className="right-header-items">
					{loggedIn === LoginStatus.LoggedIn ? (
						<>
							{!isMobile && (
								<button type="button" className="inbox-btn" aria-label="Inbox" ref={inboxButtonRef} onClick={handleOpenInboxModal}>
									<EnvelopeIcon size={22} />
									{unreadMessagesCount > 0 && <span className="unread-messages-counter">{unreadMessagesCount}</span>}
								</button>
							)}
							<div className="currency-pill">
								{currencies.map((currency) => (
									<span key={currency.key} className={`currency-item ${currency.key}`} title={currency.label}>
										{currency.icon}
										{formatCount(currency.value)}
									</span>
								))}
							</div>
							<button
								type="button"
								className={`wallet-balance ${isBalanceOpen ? 'open' : ''}`}
								ref={balanceButtonRef}
								onClick={handleOpenBalanceModal}
							>
								€ {wallet.balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
								<ChevronDownIcon className="chevron" size={18} />
							</button>
							<button
								type="button"
								className={`user-profile ${isUserOpen ? 'open' : ''}`}
								ref={userButtonRef}
								onClick={(e) => {
									e.stopPropagation()
									handleOpenUserModal()
								}}
							>
								<span className="avatar" style={{ backgroundImage: `url('${profile?.avatar_url}')` }}></span>
								<span className="level">{currentLevel?.name}</span>
								<ChevronDownIcon className="chevron" size={18} />
							</button>
							<Link className="special-btn" to="/vip">
								<CrownIcon className="icon" size={22} />
								VIP
							</Link>
						</>
					) : (
						<>
							<div className="btn" onClick={handleOpenLoginModal}>
								register
							</div>
							<div className="secondary-btn" onClick={handleOpenLoginModal}>
								{'logIn'}
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	)
}

const LANGUAGES = [
	{ code: 'en', label: 'English', flag: '🇬🇧' },
	{ code: 'br', label: 'Portuguese', flag: '🇧🇷' },
]

interface Language {
	code: string
	label: string
	flag: string
}

const LanguageDropdown = () => {
	const dispatch = useAppDispatch()
	const [isOpen, setIsOpen] = useState(false)
	const [selectedLanguage, setSelectedLanguage] = useState(LANGUAGES[0])
	const dropdownRef = useRef<HTMLDivElement>(null)

	const { handleChangeLanguage } = useTranslation(true)

	const toggleDropdown = () => setIsOpen(!isOpen)

	const handleSelectLanguage = (language: Language) => {
		setSelectedLanguage(language)
		if (handleChangeLanguage) {
			handleChangeLanguage(language.code)
  			dispatch(reInitDataForLanguage(language.code)) 
		}
		setIsOpen(false)
	}

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsOpen(false)
			}
		}
		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	return (
		<div className="language-dropdown" ref={dropdownRef}>
			<button className={`language-button ${isOpen ? 'expanded' : ''}`} onClick={toggleDropdown}>
				<span className="selected-flag">{selectedLanguage.flag}</span>
				<div className={`collapse-icon ${isOpen ? 'expanded' : ''}`} />
			</button>
			<div className={`dropdown-menu ${isOpen ? 'open' : ''}`}>
				{LANGUAGES.filter((language) => language.code !== selectedLanguage.code).map((language) => (
					<div key={language.code} className="dropdown-item" onClick={() => handleSelectLanguage(language)}>
						<span className="flag">{language.flag}</span>
						<span className="label">{language.code}</span>
					</div>
				))}
			</div>
		</div>
	)
}
